import React from 'react';
import {
  User,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  CalendarDays,
  Calendar,
  Clock,
  ArrowRight,
  ExternalLink,
  Check,
  Sparkles,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Dialog from '../shared/Dialog';
import { formatPhoneNumber, toTelHref } from '../../utils/phoneUtils';
import type { Notification } from '../../types/notification';
import {
  getNotificationVisual,
  getNotificationLink,
  isExternalUrl,
} from './notificationConfig';
import { formatRelativeTimeWithAbsolute } from '../../utils/timeUtils';

export interface NotificationDetailDialogProps {
  notification: Notification | null;
  onClose: () => void;
  onMarkRead: (notification: Notification) => void;
}

const InfoRow: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}> = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-3">
    <Icon className="h-4 w-4 text-ink-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
    <div className="min-w-0">
      <p className="text-11 uppercase tracking-wide text-ink-500 font-medium">{label}</p>
      <div className="text-[13.5px] font-medium text-ink-900 break-words">{children}</div>
    </div>
  </div>
);

const NotificationDetailDialog: React.FC<NotificationDetailDialogProps> = ({
  notification,
  onClose,
  onMarkRead,
}) => {
  const navigate = useNavigate();

  if (!notification) return null;

  const visual = getNotificationVisual(notification.type);
  const Icon = visual.icon;
  const time = formatRelativeTimeWithAbsolute(notification.timestamp);
  const meta = notification.metadata;
  const isHero = !!visual.highlight;
  const link = getNotificationLink(notification);

  const hasCustomer = !!(meta?.customerName || meta?.customerPhone || meta?.customerEmail || meta?.customerAddress);
  const hasAppointment = !!(meta?.appointmentDate || meta?.appointmentTime || meta?.serviceName);

  const handlePrimaryNav = () => {
    if (!link) return;
    if (isExternalUrl(link.to)) {
      window.open(link.to, '_blank', 'noopener,noreferrer');
    } else {
      navigate(link.to);
    }
    onClose();
  };

  const footer = (
    <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
      {!notification.isRead && (
        <button
          type="button"
          onClick={() => {
            onMarkRead(notification);
            onClose();
          }}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-13 font-medium text-ink-700 bg-surface border border-ink-300 hover:bg-ink-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-500/40 transition-colors"
        >
          <Check className="h-4 w-4" />
          Mark as read
        </button>
      )}
      {link && (
        <button
          type="button"
          onClick={handlePrimaryNav}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-13 font-medium text-white bg-ink-900 hover:bg-ink-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-900/40 transition-colors"
        >
          {link.label}
          {isExternalUrl(link.to) ? (
            <ExternalLink className="h-4 w-4" />
          ) : (
            <ArrowRight className="h-4 w-4" />
          )}
        </button>
      )}
    </div>
  );

  return (
    <Dialog
      isOpen={!!notification}
      onClose={onClose}
      hideHeader
      size="lg"
      zIndex={150}
      footer={footer}
    >
      {/* Hero strip */}
      <div
        className={`h-1 w-full ${isHero ? 'bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400' : 'bg-gradient-to-r from-blue-500 via-sky-500 to-blue-500'}`}
        aria-hidden="true"
      />

      <div className="px-6 pt-5 pb-4">
        <div className="flex items-start gap-4">
          <div
            className={`relative h-12 w-12 rounded-xl ${visual.tileBg} flex items-center justify-center flex-shrink-0`}
            aria-hidden="true"
          >
            <Icon className={`h-6 w-6 ${visual.tileIcon}`} />
            {isHero && <Sparkles className="h-3.5 w-3.5 absolute -top-1 -right-1 text-amber-500" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 mb-1">
              <span className={`inline-flex items-center text-11 font-medium rounded px-1.5 py-0.5 ${visual.pillBg} ${visual.pillText}`}>
                {visual.label}
              </span>
              {!notification.isRead && (
                <span className="inline-flex items-center text-11 font-medium rounded px-1.5 py-0.5 bg-blue-100 text-blue-700">
                  Unread
                </span>
              )}
              {notification.priority === 'high' && !isHero && (
                <span className="inline-flex items-center text-11 font-medium rounded px-1.5 py-0.5 bg-rose-100 text-rose-700">
                  Priority
                </span>
              )}
            </div>
            <h2 className="text-[17px] font-semibold text-ink-900 leading-snug">
              {notification.title}
            </h2>
            <p className="text-11 text-ink-400 mt-1" title={time.absolute}>
              {time.relative} · {time.absolute}
            </p>
          </div>
        </div>
      </div>

      <div className="px-6 pb-6 space-y-4">
        <p className="text-[13.5px] text-ink-600 leading-relaxed whitespace-pre-line">
          {notification.message}
        </p>

        {hasCustomer && (
          <section
            aria-labelledby="notification-customer"
            className="rounded-xl border border-ink-200 bg-ink-50/60 p-4"
          >
            <h3
              id="notification-customer"
              className="text-11 uppercase tracking-wide font-semibold text-ink-500 mb-3 flex items-center gap-1.5"
            >
              <User className="h-3.5 w-3.5" aria-hidden="true" />
              Customer
            </h3>
            <div className="space-y-3">
              {meta?.customerName && (
                <InfoRow icon={User} label="Name">{meta.customerName}</InfoRow>
              )}
              {meta?.customerPhone && (
                <InfoRow icon={Phone} label="Phone">
                  <a
                    href={toTelHref(meta.customerPhone)}
                    className="text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    {formatPhoneNumber(meta.customerPhone)}
                  </a>
                </InfoRow>
              )}
              {meta?.customerEmail && (
                <InfoRow icon={Mail} label="Email">
                  <a
                    href={`mailto:${meta.customerEmail}`}
                    className="text-blue-600 hover:text-blue-700 hover:underline break-all"
                  >
                    {meta.customerEmail}
                  </a>
                </InfoRow>
              )}
              {meta?.customerAddress && (
                <InfoRow icon={MapPin} label="Address">{meta.customerAddress}</InfoRow>
              )}
            </div>
          </section>
        )}

        {hasAppointment && (
          <section
            aria-labelledby="notification-appt"
            className="rounded-xl border border-ink-200 bg-ink-50/60 p-4"
          >
            <h3
              id="notification-appt"
              className="text-11 uppercase tracking-wide font-semibold text-ink-500 mb-3 flex items-center gap-1.5"
            >
              <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
              Appointment
            </h3>
            <div className="space-y-3">
              {meta?.serviceName && (
                <InfoRow icon={Briefcase} label="Service">{meta.serviceName}</InfoRow>
              )}
              {meta?.appointmentDate && (
                <InfoRow icon={Calendar} label="Date">
                  {new Date(meta.appointmentDate).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </InfoRow>
              )}
              {meta?.appointmentTime && (
                <InfoRow icon={Clock} label="Time">{meta.appointmentTime}</InfoRow>
              )}
              {meta?.previousDate && (
                <InfoRow icon={Calendar} label="Was">
                  {new Date(meta.previousDate).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                  {meta.previousTime ? ` · ${meta.previousTime}` : ''}
                </InfoRow>
              )}
              {meta?.cancellationReason && (
                <InfoRow icon={Clock} label="Reason">{meta.cancellationReason}</InfoRow>
              )}
            </div>
          </section>
        )}

        {meta?.assignedBy && (
          <div className="flex items-center justify-between text-13 rounded-xl bg-ink-50 border border-ink-200 px-4 py-3">
            <span className="text-ink-500">Assigned by</span>
            <span className="font-medium text-ink-900">{meta.assignedBy}</span>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default NotificationDetailDialog;
